<template>
  <div class="memorial-admins-page">
    <v-container class="py-10" max-width="800">
      <!-- Header -->
      <div class="page-header mb-2">
        <div class="brand-header">
          <router-link to="/">
            <PassingOnLogo class="brand-logo-svg" />
          </router-link>
        </div>
        <ProfileMenu />
      </div>

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-6"></v-progress-linear>

      <v-btn
        variant="text"
        color="primary"
        prepend-icon="mdi-arrow-left"
        class="mb-4"
        @click="goBack"
      >
        Back to Dashboard
      </v-btn>

      <v-alert v-if="errorMsg" type="error" variant="tonal" class="mb-4" closable @click:close="errorMsg = ''">
        {{ errorMsg }}
      </v-alert>

      <!-- Current Admins -->
      <v-card class="section-card mb-6" elevation="0">
        <v-card-title class="section-header primary-header">
          <v-icon left color="primary">mdi-shield-account</v-icon>
          Administrators
        </v-card-title>
        <v-card-text class="pa-6">
          <p class="text-body-1 mb-4">
            Administrators can perform all the same actions as the memory creator — editing details, managing photos and stories, approving contributions, and sharing the memory.
          </p>

          <!-- Creator -->
          <div class="admin-list mb-4">
            <div class="admin-item py-3 px-4 mb-2">
              <div class="admin-item-info">
                <v-icon color="primary" class="mr-3">mdi-account-star</v-icon>
                <div class="admin-item-text">
                  <div class="font-weight-medium">{{ creatorName }}</div>
                  <div class="text-body-2 text-medium-emphasis">{{ creatorEmail }}</div>
                </div>
              </div>
              <v-chip color="primary" size="small" variant="tonal" class="admin-item-action">Creator</v-chip>
            </div>

            <!-- Admins -->
            <div
              v-for="admin in admins"
              :key="admin.uuid"
              class="admin-item py-3 px-4 mb-2"
            >
              <div class="admin-item-info">
                <v-icon color="secondary" class="mr-3">mdi-account-check</v-icon>
                <div class="admin-item-text">
                  <div class="font-weight-medium">{{ admin.firstName }} {{ admin.surname }}</div>
                  <div class="text-body-2 text-medium-emphasis">{{ admin.email }}</div>
                </div>
              </div>
              <v-btn
                color="error"
                variant="outlined"
                size="small"
                class="admin-item-action"
                :loading="removingUuid === admin.uuid"
                @click="removeAdmin(admin)"
              >
                Remove
              </v-btn>
            </div>

            <v-alert v-if="admins.length === 0" type="info" variant="tonal" class="mt-2">
              No additional administrators have been added yet.
            </v-alert>
          </div>

          <!-- Pending Invites -->
          <div v-if="pendingInvites.length > 0">
            <div class="text-subtitle-2 mb-2 mt-4">Pending Invitations</div>
            <div
              v-for="invite in pendingInvites"
              :key="invite.token"
              class="admin-item py-3 px-4 mb-2"
            >
              <div class="admin-item-info">
                <v-icon color="warning" class="mr-3">mdi-email-clock</v-icon>
                <div class="admin-item-text">
                  <div class="text-body-2 text-medium-emphasis">{{ invite.email }}</div>
                  <div class="text-caption text-medium-emphasis">Invited {{ formatDate(invite.dateCreated) }}</div>
                </div>
              </div>
              <v-btn
                color="error"
                variant="text"
                size="small"
                class="admin-item-action"
                :loading="cancellingToken === invite.token"
                @click="cancelInvite(invite)"
              >
                Cancel
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Add Admin -->
      <v-card class="section-card mb-6" elevation="0">
        <v-card-title class="section-header primary-header">
          <v-icon left color="primary">mdi-account-plus</v-icon>
          Add Administrator
        </v-card-title>
        <v-card-text class="pa-6">
          <p class="text-body-1 mb-4">
            Enter the email address of the person you want to add as an administrator. If they already have an account, they'll be added immediately. Otherwise, they'll receive an invitation by email.
          </p>

          <div class="d-flex align-start ga-3">
            <v-text-field
              v-model="newAdminEmail"
              label="Email address"
              type="email"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              :error-messages="addErrorMsg"
              :disabled="addingAdmin"
              class="flex-grow-1"
              @keyup.enter="addAdmin"
            />
            <v-btn
              color="primary"
              variant="outlined"
              height="50"
              :loading="addingAdmin"
              :disabled="!newAdminEmail.trim()"
              @click="addAdmin"
            >
              Add As Admin
            </v-btn>
          </div>

          <v-alert
            v-if="addSuccessMsg"
            type="success"
            variant="tonal"
            class="mt-4"
            closable
            @click:close="addSuccessMsg = ''"
          >
            {{ addSuccessMsg }}
          </v-alert>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import { useMemorialStore } from '@/stores/memorial'
import api from '@/services/api'
import PassingOnLogo from '@/components/PassingOnLogo.vue'
import ProfileMenu from '@/components/ProfileMenu.vue'

export default {
  name: 'MemorialAdminsPage',
  components: { PassingOnLogo, ProfileMenu },

  data() {
    return {
      loading: false,
      admins: [],
      pendingInvites: [],
      creatorName: '',
      creatorEmail: '',
      newAdminEmail: '',
      addingAdmin: false,
      addErrorMsg: '',
      addSuccessMsg: '',
      errorMsg: '',
      removingUuid: null,
      cancellingToken: null
    }
  },

  computed: {
    store() {
      return useMemorialStore()
    },
    memorialUuid() {
      return this.store.memorialUuid
    },
    token() {
      return this.store.apiToken
    }
  },

  async mounted() {
    if (!this.memorialUuid || !this.token) {
      this.$router.push('/memory/dashboard')
      return
    }
    await this.loadAdmins()
  },

  methods: {
    async loadAdmins() {
      this.loading = true
      this.errorMsg = ''
      try {
        const result = await api.listMemorialAdmins(this.memorialUuid, this.token)
        if (result.success) {
          this.admins = result.admins || []
          this.pendingInvites = result.pendingInvites || []
        } else {
          this.errorMsg = result.msg || 'Failed to load administrators'
        }

        // Also load creator info from the memorial
        const memorialResult = await api.getMemorial(this.memorialUuid)
        if (memorialResult.success && memorialResult.memorial?.user) {
          const creator = memorialResult.memorial.user
          this.creatorName = [creator.firstName, creator.surname].filter(Boolean).join(' ') || creator.email
          this.creatorEmail = creator.email
        }
      } catch (e) {
        this.errorMsg = 'Failed to load administrators'
      } finally {
        this.loading = false
      }
    },

    async addAdmin() {
      const email = this.newAdminEmail.trim()
      if (!email) return

      this.addingAdmin = true
      this.addErrorMsg = ''
      this.addSuccessMsg = ''

      try {
        const result = await api.addMemorialAdmin(this.memorialUuid, email, this.token)
        if (result.success) {
          this.addSuccessMsg = result.msg
          this.newAdminEmail = ''
          await this.loadAdmins()
        } else {
          this.addErrorMsg = result.msg || 'Failed to add administrator'
        }
      } catch (e) {
        this.addErrorMsg = 'An error occurred. Please try again.'
      } finally {
        this.addingAdmin = false
      }
    },

    async removeAdmin(admin) {
      this.removingUuid = admin.uuid
      this.errorMsg = ''
      try {
        const result = await api.removeMemorialAdmin(this.memorialUuid, admin.uuid, this.token)
        if (result.success) {
          await this.loadAdmins()
        } else {
          this.errorMsg = result.msg || 'Failed to remove administrator'
        }
      } catch (e) {
        this.errorMsg = 'An error occurred. Please try again.'
      } finally {
        this.removingUuid = null
      }
    },

    async cancelInvite(invite) {
      this.cancellingToken = invite.token
      this.errorMsg = ''
      try {
        const result = await api.cancelMemorialAdminInvite(this.memorialUuid, invite.token, this.token)
        if (result.success) {
          await this.loadAdmins()
        } else {
          this.errorMsg = result.msg || 'Failed to cancel invitation'
        }
      } catch (e) {
        this.errorMsg = 'An error occurred. Please try again.'
      } finally {
        this.cancellingToken = null
      }
    },

    formatDate(ts) {
      if (!ts) return ''
      return new Date(ts).toLocaleDateString()
    },

    goBack() {
      this.$router.push('/memory/dashboard')
    }
  }
}
</script>

<style scoped>
.section-header {
  background: white;
  color: var(--v-theme-primary);
  font-weight: 600;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(0,0,0,0.08);
}

.primary-header {
  background: white;
}

.section-card {
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 8px;
  overflow: hidden;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.brand-logo-svg {
  height: 40px;
}

.admin-item {
  background: rgba(0,0,0,0.02);
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.admin-item-info {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.admin-item-text {
  min-width: 0;
}

.admin-item-text .font-weight-medium,
.admin-item-text .text-body-2,
.admin-item-text .text-caption {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 600px) {
  .admin-item {
    flex-direction: column;
    align-items: stretch;
  }
  .admin-item-action {
    align-self: flex-end;
  }
}

:deep(.v-text-field) {
  margin-top: 0;
}
</style>
